import React, { useState, useRef, useEffect } from "react";

/**
 * A select dropdown that renders options in a scrollable div so the list
 * stays inside the popup and does not overflow outside (unlike native <select>).
 */
export function ContainedSelect({
  value = "",
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  className = "",
  style = {},
  id,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selectedLabel = value
    ? (options.find((o) => o.value === value)?.label ?? value)
    : "";

  const baseClass =
    "w-full rounded-[10px] border border-[#D0D5DD] bg-white px-3 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[rgba(24,135,66,0.12)] appearance-none flex items-center justify-between";
  const disabledClass = disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer";

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        id={id}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`${baseClass} ${disabledClass} ${className}`}
        style={{ height: 44, ...style }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabel ? "text-gray-800 truncate" : "text-[#98A2B3]"}>
          {selectedLabel || placeholder}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className={`h-4 w-4 text-gray-400 shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && !disabled && (
        <div
          className="absolute z-[9999] left-0 right-0 mt-1 rounded-[10px] border border-[#D0D5DD] bg-white shadow-lg overflow-hidden"
          role="listbox"
        >
          <div
            className="overflow-y-auto overscroll-contain py-1"
            style={{ maxHeight: 220 }}
          >
            <button
              type="button"
              role="option"
              onClick={() => {
                onChange({ target: { value: "" } });
                setOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-[#F6F7FA] ${!value ? "bg-[#F6F7FA] text-gray-800" : "text-gray-600"}`}
            >
              {placeholder}
            </button>
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                onClick={() => {
                  onChange({ target: { value: opt.value } });
                  setOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-[#F6F7FA] truncate ${value === opt.value ? "bg-[rgba(24,135,66,0.08)] text-[#188742]" : "text-gray-800"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
